import { Box, Chip, Stack, Typography, useTheme } from '@mui/material'
import { UIStatus, WaterSourceType } from '../../../../store/waterQualityIndex'

export type PillarKey = 'test' | 'monitor' | 'protect'

export interface PillarCardProps {
  pillar: PillarKey
  waterSafetyStatus: UIStatus
  waterSourceType: WaterSourceType
  utilityViolationCount: number | null
  utilityHasMicrobiologicalViolations: boolean
  utilityHasCommunicationViolations: boolean
  // SIPP-826: true when the utility has at least one health-based violation
  // (MCL, TT, microbiological, lead, arsenic, nitrate, etc.). Derived
  // upstream so the TEST callout can surface safety issues even when the
  // utility ALSO has communication-only violations — the old
  // `!utilityHasCommunicationViolations` shortcut incorrectly masked the
  // safety branch whenever both kinds coexisted.
  utilityHasHealthBasedViolations: boolean
  // SIPP-828: true when the user answered "None / I don't use a filter" in
  // the test strip equipment question. Drives the Protect pillar's unfiltered
  // conversion copy.
  userHasNoFilter: boolean
  // SIPP-841: home_age string from the latest test kit request (e.g. "20-25",
  // "26+", or an exact year string). Used inside this component to derive
  // isOlderHome / isNewerHome buckets for the Smart Home upgrade triggers.
  homeAge: string | null
  // SIPP-841: true when the user has a `chloramine_plumbing_stress_context`
  // signal anywhere in their WQI report — the chloramine + PEX risk signal.
  hasChloramineStress: boolean
  unsafeCount: number
  cautiousCount: number
}

interface CopyBlock {
  callout: string
  without: string
  with: string
  // SIPP-841: optional appended sentence + chip when one of the four Smart
  // Home upgrade triggers fires. Conditions 3 (older home → MONITOR) and 4
  // (newer + chloramine → PROTECT) get this treatment. Conditions 1 + 2
  // (well water / microbiological) already have entirely Smart-Home-focused
  // With-SIPP copy so they don't need the appended chip.
  extraWith?: string
  showSmartHomeChip?: boolean
}

// SIPP-841: parse the test strip flow's `home_age` string into year-built
// buckets. Supports the strip flow's age ranges ("0-5" through "26+") and
// raw 4-digit year strings. Anything unparseable returns both flags false
// so the upgrade triggers stay quiet rather than misfiring.
const HOME_AGE_THRESHOLDS = {
  olderHomeMaxYearBuilt: 1979, // pre-lead-ban era
  newerHomeMinYearBuilt: 2000, // PEX era
}

export const deriveHomeAgeFlags = (
  homeAge: string | null
): { isOlderHome: boolean; isNewerHome: boolean } => {
  if (!homeAge) return { isOlderHome: false, isNewerHome: false }
  const trimmed = homeAge.trim()
  const currentYear = new Date().getFullYear()

  // Exact 4-digit year (e.g. "1968", "2015")
  if (/^\d{4}$/.test(trimmed)) {
    const year = parseInt(trimmed, 10)
    return {
      isOlderHome: year <= HOME_AGE_THRESHOLDS.olderHomeMaxYearBuilt,
      isNewerHome: year >= HOME_AGE_THRESHOLDS.newerHomeMinYearBuilt,
    }
  }

  // Open-ended max range "26+" → home is 26+ years old. Within the strip
  // flow's bucket set this is the only bucket that can include pre-1979
  // homes, so flag as older.
  if (/^\d+\+$/.test(trimmed)) {
    const minAge = parseInt(trimmed, 10)
    const maxYearBuilt = currentYear - minAge
    return {
      isOlderHome: maxYearBuilt <= HOME_AGE_THRESHOLDS.olderHomeMaxYearBuilt
        ? true
        : minAge >= 26, // bucket-level fallback for the strip flow
      isNewerHome: false,
    }
  }

  // Closed range "0-5", "5-10", etc. Use the upper bound to compute the
  // earliest possible build year.
  const rangeMatch = trimmed.match(/^(\d+)\s*-\s*(\d+)$/)
  if (rangeMatch) {
    const maxAge = parseInt(rangeMatch[2], 10)
    const minYearBuilt = currentYear - maxAge
    return {
      isOlderHome: minYearBuilt <= HOME_AGE_THRESHOLDS.olderHomeMaxYearBuilt,
      isNewerHome: minYearBuilt >= HOME_AGE_THRESHOLDS.newerHomeMinYearBuilt,
    }
  }

  return { isOlderHome: false, isNewerHome: false }
}

// SIPP-840: PROTECT "Without" copy reframed — SIPP doesn't match filters,
// it ensures the right standard NSF cartridge gets to the user on the right
// schedule. The previous "no filter matching" line implied a service we
// don't actually offer.
const WITHOUT_COPY: Record<PillarKey, string> = {
  test:
    "No reminders. You'd need to order, schedule, and track your own tests — most people don't.",
  monitor:
    "Your utility reports annually at most. You'd need to check their site manually — and changes could go months before you'd know.",
  protect:
    "No reminders, no schedule. You'd need to track when to replace your filter yourself — and find the right one for your equipment. Most people let it go too long.",
}

const buildCopy = (props: PillarCardProps): CopyBlock => {
  const {
    pillar,
    waterSourceType,
    utilityViolationCount,
    utilityHasMicrobiologicalViolations,
    utilityHasCommunicationViolations,
    utilityHasHealthBasedViolations,
    unsafeCount,
    cautiousCount,
  } = props
  const isWell = waterSourceType === 'well'
  // SIPP-826: evaluate safety and communication flags independently. A
  // utility can have both — previously the `&& !utilityHasCommunicationViolations`
  // clause swallowed the safety branch whenever any communication violation
  // was on record, so the TEST callout fell back to generic monitoring copy
  // even when the more urgent health issue should have been surfaced.
  const hasSafetyViolations =
    (utilityViolationCount ?? 0) > 0 && utilityHasHealthBasedViolations

  if (pillar === 'test') {
    let callout: string
    if (isWell) {
      callout =
        "You're on well water — no utility is testing on your behalf. Routine testing is your only line of defense."
    } else if (hasSafetyViolations && utilityViolationCount) {
      callout = `Your utility has logged ${utilityViolationCount} violation(s) in the past 5 years, including health-based issues.`
    } else {
      callout = `Your results showed ${unsafeCount + cautiousCount} readings that need monitoring between tests.`
    }

    let withSipp: string
    if (isWell) {
      withSipp =
        'Quarterly test kits sent automatically. Because you\'re on well water, we treat every test as essential — no utility is backing you up.'
    } else if ((utilityViolationCount ?? 0) > 0) {
      withSipp =
        'Quarterly test kits sent automatically. Because your utility has violation history, we flag any new drift toward unsafe before it becomes a problem.'
    } else {
      withSipp =
        'Quarterly test kits sent automatically, with same-day digital results. No lab visits, no shipping delays.'
    }

    return { callout, without: WITHOUT_COPY.test, with: withSipp }
  }

  if (pillar === 'monitor') {
    const { isOlderHome } = deriveHomeAgeFlags(props.homeAge)
    let callout: string
    if (isOlderHome) {
      // SIPP-841 condition 3: older home corrosion risk takes precedence in
      // the MONITOR callout — it justifies the appended Smart Home upgrade.
      callout =
        "Your home's age suggests older pipe materials that can corrode over time — making early leak detection especially important."
    } else if (utilityHasCommunicationViolations) {
      callout =
        'Your utility has logged communication violations — they may not proactively tell you when something changes.'
    } else if ((utilityViolationCount ?? 0) > 0 && unsafeCount + cautiousCount > 0) {
      callout = `You have ${unsafeCount + cautiousCount} flagged readings and your utility has a violation history. Changes could go unnoticed.`
    } else {
      callout =
        'Utility data updates happen without notice. A shift in your area could affect your results before your next test.'
    }
    return {
      callout,
      without: WITHOUT_COPY.monitor,
      with: 'Continuous utility monitoring plus regional alerts powered by USGS data and community reports. If anything shifts near you, we tell you directly.',
      extraWith: isOlderHome
        ? 'The Smart Home Water Monitor detects early signs of corrosion and pressure drops that precede leaks — potentially saving thousands in plumbing repairs.'
        : undefined,
      showSmartHomeChip: isOlderHome,
    }
  }

  // pillar === 'protect'
  // SIPP-828: unfiltered users get their own branch — the conversion moment
  // is "here's what's in the water you're drinking right now" rather than
  // "here's a filter upgrade for the setup you already have".
  // SIPP-840: copy reframed for accuracy — SIPP doesn't match filters, it
  // ensures the right replacement cartridge arrives on the right schedule.
  if (props.userHasNoFilter) {
    return {
      callout: `You're currently drinking unfiltered tap water. Your results flagged ${unsafeCount + cautiousCount} contaminants that a standard filter would address.`,
      without:
        "You'd need to research which filter type addresses your specific results, find a retailer, and remember to replace it on schedule — most people don't get this right without help.",
      with:
        "We make sure you have the right replacement filter for your home — the correct NSF standard, delivered on schedule. No research, no forgetting, no letting it go past its prime.",
    }
  }
  const needsDisinfectionProtection = isWell || utilityHasMicrobiologicalViolations
  const { isNewerHome } = deriveHomeAgeFlags(props.homeAge)
  // SIPP-841 condition 4: newer home + chloramine = PEX corrosion risk.
  const isPexChloramineRisk = isNewerHome && props.hasChloramineStress

  let callout: string
  if (needsDisinfectionProtection) {
    callout =
      "Your water source requires disinfection-level protection that standard filters don't provide."
  } else if (isPexChloramineRisk) {
    callout =
      'Your home was built after 2000, likely with PEX piping. Your utility uses chloramine — which is known to react with PEX and can cause pinhole leaks over time.'
  } else {
    callout =
      'Your current equipment covers some contaminants — but your results flagged issues beyond that range.'
  }

  // Well-water / microbiological branch keeps the Smart Home recommendation
  // language; that path is about disinfection, not filter replacement.
  const withSipp = needsDisinfectionProtection
    ? "We strongly recommend the Smart Home Water Monitor for your setup — it provides continuous disinfection detection that a standard filter can't match."
    : "We make sure you have the right replacement filter for your equipment — the correct NSF standard, delivered on schedule. No research, no forgetting, no letting it go past its prime."

  return {
    callout,
    without: WITHOUT_COPY.protect,
    with: withSipp,
    extraWith: isPexChloramineRisk
      ? 'The Smart Home Water Monitor continuously tracks water quality and pressure changes that signal early PEX degradation — catching problems before they become costly repairs.'
      : undefined,
    showSmartHomeChip: isPexChloramineRisk,
  }
}

// SIPP-830: each pillar gets a distinct theme color so the sections are
// scannable at a glance. `calloutAccent` is the left-border accent on the
// data callout box and ties that insight back to its pillar visually.
const PILL_STYLES: Record<
  PillarKey,
  { label: string; bg: string; color: string; calloutAccent: string }
> = {
  test: {
    label: 'TEST',
    bg: 'primary.light',
    color: 'primary.dark',
    calloutAccent: 'primary.main',
  },
  monitor: {
    label: 'MONITOR',
    bg: 'warning.light',
    color: 'warning.dark',
    calloutAccent: 'warning.main',
  },
  protect: {
    label: 'PROTECT',
    bg: 'success.light',
    color: 'success.dark',
    calloutAccent: 'success.main',
  },
}

export const PillarCard = (props: PillarCardProps) => {
  const theme = useTheme()
  const copy = buildCopy(props)
  const pill = PILL_STYLES[props.pillar]

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        height: '100%',
      }}
    >
      <Stack spacing={1.5}>
        <Box
          sx={{
            alignSelf: 'flex-start',
            bgcolor: pill.bg,
            color: pill.color,
            px: 1.25,
            py: 0.5,
            borderRadius: 5,
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}
          >
            {pill.label}
          </Typography>
        </Box>

        {/* SIPP-830: data callout sits above the Without/With stack with a
            distinct elevated treatment — white background, soft divider
            border, and a left-border accent in the pillar's color so the
            insight ties back to its pillar visually. */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderLeft: '3px solid',
            borderLeftColor: pill.calloutAccent,
            borderRadius: 1.5,
            p: 1.5,
          }}
        >
          <Typography variant="body2" color="text.primary">
            {copy.callout}
          </Typography>
        </Box>

        <Box sx={{ bgcolor: 'grey.100', borderRadius: 1.5, p: 1.5 }}>
          <Typography
            variant="caption"
            sx={{ display: 'block', fontWeight: 600 }}
            color="text.secondary"
          >
            Without SIPP
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {copy.without}
          </Typography>
        </Box>

        <Box
          sx={{
            bgcolor: theme.palette.success.bgcolor,
            borderRadius: 1.5,
            p: 1.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{ display: 'block', fontWeight: 600 }}
            color="success.dark"
          >
            With SIPP Pro
          </Typography>
          <Typography variant="body2" color="text.primary">
            {copy.with}
          </Typography>
          {/* SIPP-841: appended Smart Home upgrade copy + chip when one of
              the conditional triggers (older home in MONITOR, newer + chloramine
              in PROTECT) fires. */}
          {copy.extraWith && (
            <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
              {copy.extraWith}
            </Typography>
          )}
          {copy.showSmartHomeChip && (
            <Chip
              label="Smart Home recommended"
              size="small"
              sx={{
                bgcolor: 'primary.darker',
                color: 'common.white',
                mt: 1,
                fontWeight: 600,
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            alignSelf: 'flex-start',
            bgcolor: 'success.light',
            borderRadius: 1.5,
            px: 1.5,
            py: 0.5,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600 }} color="success.dark">
            ✓ Included in SIPP Pro
          </Typography>
        </Box>
      </Stack>
    </Box>
  )
}
